import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';

import {
  useGetProductsSubProductTemplatesQuery,
  SubProductCategoryTemplateReadModel,
} from '@client/api';
import {
  Button,
  Input,
  Modal,
  ModalContent,
  ModalFooter,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  Text,
  Typography,
} from '@client/toolkit';

interface SubProductAddModalProps {
  open: boolean;
  existingChildren: Array<{ id: string; name: string }>;
  onAddTemplate: (template: SubProductCategoryTemplateReadModel) => Promise<void>;
  onAddCustom: (name: string) => Promise<void>;
  /**
   * Mark a sub-product for deletion on the outer card. Sync (no API call) — the actual
   * DELETE fires when the user clicks Lagre on the outer card, consistent with the
   * outer trash button.
   */
  onRemoveChild: (childId: string) => void;
  onClose: () => void;
}

export function SubProductAddModal({
  open,
  existingChildren,
  onAddTemplate,
  onAddCustom,
  onRemoveChild,
  onClose,
}: SubProductAddModalProps) {
  const { t } = useTranslation('products');
  const { t: tCommon } = useTranslation();

  const [customName, setCustomName] = useState('');
  const [selectedStandard, setSelectedStandard] = useState('NO');
  const [isBulkBusy, setIsBulkBusy] = useState(false);

  const { data: templates, isLoading: isLoadingTemplates } = useGetProductsSubProductTemplatesQuery(
    { countryCode: selectedStandard },
    { skip: !open },
  );

  // Map template-name -> existing child (only templates that have a matching added child).
  // Used for both the per-row remove button and the "Fjern alle" bulk action.
  const childByTemplateName = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    existingChildren.forEach((c) => map.set(c.name, c));
    return map;
  }, [existingChildren]);

  const templatesNotAdded = useMemo(
    () => (templates ?? []).filter((tpl) => !childByTemplateName.has(tpl.name)),
    [templates, childByTemplateName],
  );
  const templatesAdded = useMemo(
    () => (templates ?? []).filter((tpl) => childByTemplateName.has(tpl.name)),
    [templates, childByTemplateName],
  );

  const handleAddFromTemplate = async (template: SubProductCategoryTemplateReadModel) => {
    try {
      await onAddTemplate(template);
      toast.success(tCommon('saved'));
    } catch {
      toast.error(tCommon('error'));
    }
  };

  const handleRemoveFromTemplate = (template: SubProductCategoryTemplateReadModel) => {
    const child = childByTemplateName.get(template.name);
    if (!child) return;
    onRemoveChild(child.id);
    // No toast — consistent with the outer trash button which also marks pending silently.
  };

  const handleAddAll = async () => {
    if (templatesNotAdded.length === 0) return;
    setIsBulkBusy(true);
    try {
      // Sequential to keep RTK-Query cache invalidation predictable and avoid 429-ish spikes.
      for (const tpl of templatesNotAdded) {
        await onAddTemplate(tpl);
      }
      toast.success(tCommon('saved'));
    } catch {
      toast.error(tCommon('error'));
    } finally {
      setIsBulkBusy(false);
    }
  };

  const handleRemoveAll = () => {
    if (templatesAdded.length === 0) return;
    for (const tpl of templatesAdded) {
      const child = childByTemplateName.get(tpl.name);
      if (child) onRemoveChild(child.id);
    }
  };

  const handleAddCustom = async () => {
    const trimmed = customName.trim();
    if (!trimmed) return;
    try {
      await onAddCustom(trimmed);
      toast.success(tCommon('saved'));
      setCustomName('');
    } catch {
      toast.error(tCommon('error'));
    }
  };

  const handleClose = () => {
    setCustomName('');
    onClose();
  };

  return (
    <Modal open={open} appear size="2xl" onClose={handleClose} title={t('subProducts.addTitle')}>
      <ModalContent className="divide-solid divide-y">
        {/* Template picker */}
        <article className="pb-6 px-4 space-y-4">
          <div className="flex items-center gap-3">
            <Typography variant="label" className="whitespace-nowrap">
              {t('subProducts.templateLabel')}
            </Typography>
            <div className="w-1/3">
              <Select
                value={selectedStandard}
                onChange={(e) => setSelectedStandard(e.target.value)}
              >
                <option value="NO">{t('subProducts.standards.NO')}</option>
              </Select>
            </div>
            <div className="flex-1" />
            <Button
              size="xs"
              color="primary-dark"
              className="whitespace-nowrap"
              onClick={handleAddAll}
              disabled={isBulkBusy || templatesNotAdded.length === 0}
            >
              Legg til alle
            </Button>
            <Button
              size="xs"
              color="primary-invert"
              className="whitespace-nowrap"
              onClick={handleRemoveAll}
              disabled={isBulkBusy || templatesAdded.length === 0}
            >
              Fjern alle
            </Button>
          </div>

          <Table className="overflow-hidden rounded-md">
            <TableHead>
              <TableHeadRow>
                <TableHeadCell>{t('subProducts.name')}</TableHeadCell>
                <TableHeadCell>{t('subProducts.benchmarkAvg')}</TableHeadCell>
                <TableHeadCell>{t('subProducts.benchmarkRange')}</TableHeadCell>
                <TableHeadCell className="w-[1%]" />
              </TableHeadRow>
            </TableHead>

            <TableBody>
              {isLoadingTemplates &&
                Array.from(Array(5).keys()).map((x) => (
                  <TableRow key={x}>
                    <TableCell loading />
                    <TableCell loading />
                    <TableCell loading />
                    <TableCell loading />
                  </TableRow>
                ))}

              {!isLoadingTemplates &&
                templates?.map((template) => {
                  const isAdded = childByTemplateName.has(template.name);
                  return (
                    <TableRow key={template.id} className={isAdded ? 'opacity-50' : ''}>
                      <TableCell>
                        <Text className="text-sm font-medium whitespace-nowrap">
                          {template.name}
                        </Text>
                      </TableCell>
                      <TableCell>
                        <Text className="text-sm font-normal whitespace-nowrap">
                          {template.benchmarkAverage} kr/m²
                        </Text>
                      </TableCell>
                      <TableCell>
                        <Text className="text-sm font-normal whitespace-nowrap text-secondary">
                          {template.benchmarkLow} – {template.benchmarkHigh}
                        </Text>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {isAdded ? (
                          <Button
                            size="xs"
                            color="primary-invert"
                            className="whitespace-nowrap"
                            onClick={() => handleRemoveFromTemplate(template)}
                            disabled={isBulkBusy}
                          >
                            {tCommon('remove')}
                          </Button>
                        ) : (
                          <Button
                            size="xs"
                            color="primary-dark"
                            className="whitespace-nowrap"
                            onClick={() => handleAddFromTemplate(template)}
                            disabled={isBulkBusy}
                          >
                            {tCommon('add')}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </article>

      </ModalContent>

      <ModalFooter>
        <Button color="primary-dark" onClick={handleClose}>
          {tCommon('close')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
